import { Controller } from '@hotwired/stimulus';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

const DEFAULT_CENTER = { lat: 50.0755, lng: 14.4378 };
const MARKER_ICON_URL = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
	'<svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.9995 0C5.3833 0 0 5.28508 0 11.7812C0 13.4757 0.35326 15.0935 1.04944 16.5901C4.04965 23.0355 9.80188 29.8405 11.4938 31.7724C11.6206 31.9168 11.8054 32 11.9998 32C12.1943 32 12.3791 31.9168 12.5059 31.7724C14.1971 29.8408 19.9494 23.0362 22.9506 16.5901C23.6471 15.0935 24 13.4757 24 11.7812C23.9993 5.28508 18.616 0 11.9995 0ZM11.9995 17.9004C8.56264 17.9004 5.76624 15.1551 5.76624 11.7809C5.76624 8.40642 8.56264 5.66104 11.9995 5.66104C15.4364 5.66104 18.2328 8.40642 18.2328 11.7809C18.2331 15.1551 15.4367 17.9004 11.9995 17.9004Z" fill="#436AB3"/></svg>',
)}`;
const MARKER_ICON_ACTIVE_URL = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
	'<svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.9995 0C5.3833 0 0 5.28508 0 11.7812C0 13.4757 0.35326 15.0935 1.04944 16.5901C4.04965 23.0355 9.80188 29.8405 11.4938 31.7724C11.6206 31.9168 11.8054 32 11.9998 32C12.1943 32 12.3791 31.9168 12.5059 31.7724C14.1971 29.8408 19.9494 23.0362 22.9506 16.5901C23.6471 15.0935 24 13.4757 24 11.7812C23.9993 5.28508 18.616 0 11.9995 0ZM11.9995 17.9004C8.56264 17.9004 5.76624 15.1551 5.76624 11.7809C5.76624 8.40642 8.56264 5.66104 11.9995 5.66104C15.4364 5.66104 18.2328 8.40642 18.2328 11.7809C18.2331 15.1551 15.4367 17.9004 11.9995 17.9004Z" fill="#E22F27"/></svg>',
)}`;
const CLUSTER_MARKER_ICON_URL = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
	'<svg width="36" height="48" viewBox="0 0 36 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.9993 0C8.07494 0 0 7.92762 0 17.6719C0 20.2135 0.52989 22.6402 1.57416 24.8852C6.07447 34.5533 14.7028 44.7608 17.2407 47.6586C17.4308 47.8752 17.708 48 17.9998 48C18.2915 48 18.5687 47.8752 18.7588 47.6586C21.2957 44.7613 29.924 34.5543 34.4258 24.8852C35.4706 22.6402 36 20.2135 36 17.6719C35.999 7.92762 27.9241 0 17.9993 0ZM17.9993 26.8507C12.844 26.8507 8.64937 22.7326 8.64937 17.6714C8.64937 12.6096 12.844 8.49157 17.9993 8.49157C23.1545 8.49157 27.3491 12.6096 27.3491 17.6714C27.3496 22.7326 23.155 26.8507 17.9993 26.8507Z" fill="#436AB3"/><rect x="3" y="3" width="30" height="30" rx="15" fill="white"/></svg>',
)}`;
const CLUSTER_MARKER_ICON_ACTIVE_URL = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
	'<svg width="36" height="48" viewBox="0 0 36 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.9993 0C8.07494 0 0 7.92762 0 17.6719C0 20.2135 0.52989 22.6402 1.57416 24.8852C6.07447 34.5533 14.7028 44.7608 17.2407 47.6586C17.4308 47.8752 17.708 48 17.9998 48C18.2915 48 18.5687 47.8752 18.7588 47.6586C21.2957 44.7613 29.924 34.5543 34.4258 24.8852C35.4706 22.6402 36 20.2135 36 17.6719C35.999 7.92762 27.9241 0 17.9993 0ZM17.9993 26.8507C12.844 26.8507 8.64937 22.7326 8.64937 17.6714C8.64937 12.6096 12.844 8.49157 17.9993 8.49157C23.1545 8.49157 27.3491 12.6096 27.3491 17.6714C27.3496 22.7326 23.155 26.8507 17.9993 26.8507Z" fill="#E22F27"/><rect x="3" y="3" width="30" height="30" rx="15" fill="white"/></svg>',
)}`;
const GRAYSCALE_MAP_STYLES = [
	{ elementType: 'geometry', stylers: [{ saturation: -100 }, { lightness: 12 }] },
	{ elementType: 'labels.icon', stylers: [{ saturation: -100 }] },
	{ elementType: 'labels.text.fill', stylers: [{ color: '#5f6368' }] },
	{ elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
	{ featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#d6d6d6' }] },
	{ featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }, { weight: 1.2 }] },
	{ featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ color: '#1f1f1f' }, { weight: 0.9 }] },
	{ featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#e8e8e8' }] },
	{ featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#dcdcdc' }] },
	{ featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
	{ featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ececec' }] },
	{ featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#878787' }] },
	{ featureType: 'water', elementType: 'geometry', stylers: [{ color: '#cfcfcf' }] },
];
export const create = () => {
	return class extends Controller {
		static targets = [
			'accessNote',
			'authModeInput',
			'alumniScopeButton',
			'alumniScopePanel',
			'canvas',
			'filterPanel',
			'fullscreenOverlay',
			'layerSelect',
			'liveStatus',
			'mapWrapper',
			'placeholder',
			'select',
			'status',
			'summary',
			'toggleFilterButton',
		];
		static values = {
			antiforgeryToken: String,
			apiKey: String,
			dataUrl: String,
			defaultLayer: String,
			employerListingUrl: String,
			labels: Object,
			lat: Number,
			lng: Number,
			updateCoordinatesUrl: String,
			zoom: Number,
			surface: String,
		};

		connect() {
			this.dataset = null;
			this.activeLayer = null;
			this.alumniScope = 'origin';
			this.datasetAuthenticated = false;
			this.filterKeys = [];
			this.geocodeCache = new Map();
			this.geocoder = null;
			this.googleMaps = null;
			this.isAuthenticated = false;
			this.infoWindow = null;
			this.map = null;
			this.clusterer = null;
			this.markerInstances = [];
			this.coordinateUpdateQueue = new Map();
			this.hasSubmittedCoordinateUpdates = false;
			this.activeInfoMarker = null;
			this.activeInfoMarkerDefaultIcon = null;
			this.renderRequestId = 0;
			this._lastDisplayCount = 0;
			this._onMapPointerDown = (event) => {
				if (event.pointerType === 'touch' || window.innerWidth < 768) {
					this.hideMobileNavigationControls();
				}
			};
			this.canvasTarget?.addEventListener('pointerdown', this._onMapPointerDown, { passive: true });
			this.initialize();
		}

		disconnect() {
			this.canvasTarget?.removeEventListener('pointerdown', this._onMapPointerDown);
		}

		async initialize() {
			this.disableSelects();
			this.updateStatus(this.text('loadingData', 'Loading data'));

			const [datasetResult, mapsResult] = await Promise.allSettled([this.loadDataset(), this.loadGoogleMaps()]);

			if (datasetResult.status !== 'fulfilled') {
				this.renderSummary([], this.text('mapDataFailed', 'Failed to load map data.'));
				this.updateStatus(this.text('dataError', 'Data error'));
				return;
			}

			this.dataset = datasetResult.value;
			this.datasetAuthenticated = Boolean(this.dataset?.settings?.isAuthenticated);
			this.isAuthenticated = this.datasetAuthenticated;
			this.configureLayer();
			this.configureLayerSelect();
			this.configureAccessNote();
			this.populateFilters();
			this.syncLayerUi();
			this.renderSummary();

			if (mapsResult.status !== 'fulfilled' || !mapsResult.value) {
				this.updateStatus(
					this.apiKeyValue ? this.text('mapUnavailable', 'Map is unavailable') : this.text('apiKeyMissing', 'API key is missing'),
				);
				return;
			}

			this.googleMaps = mapsResult.value;
			this.initializeMap();
			await this.renderMarkers();
			await this.flushCoordinateUpdates();
		}

		applyFilters(event) {
			event.preventDefault();
			const filters = this.collectFilters();
			this.renderSummary(filters);
			this.renderMarkers();
			this.dispatch('filter', { detail: { count: this._lastDisplayCount, filters } });

			// Zavři filtry na mobilech po aplikaci
			if (window.innerWidth < 768 && this.hasFilterPanelTarget && !this.filterPanelTarget.classList.contains('hidden')) {
				this.filterPanelTarget.classList.add('hidden');
				// Aktualizuj texty na tlačítcích
				this.toggleFilterButtonTargets.forEach((button) => {
					const showText = button.dataset.mapShowText;
					const textSpan = button.querySelector('[data-text-content]');
					if (textSpan) {
						textSpan.textContent = showText;
					}
					button.setAttribute('aria-expanded', 'false');
				});
			}
		}

		switchLayer(event) {
			const nextLayer = this.hasLayerSelectTarget ? this.layerSelectTarget.value : event.currentTarget?.dataset?.mapLayer;
			if (!nextLayer || nextLayer === this.activeLayer) return;

			this.activeLayer = nextLayer;
			if (this.activeLayer !== 'alumni') this.alumniScope = 'origin';
			this.filterKeys = this.getLayerFilterKeys();
			this.populateFilters();
			this.syncLayerUi();
			this.renderSummary();
			this.renderMarkers();
		}

		switchAlumniScope(event) {
			event.preventDefault();
			if (this.activeLayer !== 'alumni' || !this.isAuthenticated) return;

			const nextScope = event.currentTarget?.dataset?.mapScope;
			if (!nextScope || nextScope === this.alumniScope) return;

			this.alumniScope = nextScope;
			this.syncLayerUi();
			this.renderSummary();
			this.renderMarkers();
		}

		setAuthenticationState(event) {
			const nextMode = event.currentTarget?.value;
			if (!nextMode) return;

			this.isAuthenticated = nextMode === 'registered';
			if (!this.isAuthenticated) this.alumniScope = 'origin';

			this.configureAccessNote();
			this.syncLayerUi();
			this.renderSummary();
			this.renderMarkers();
		}

		async loadDataset() {
			if (!this.dataUrlValue) throw new Error('Missing map data URL');

			window.__alumniMapDatasetPromises ??= new Map();

			if (!window.__alumniMapDatasetPromises.has(this.dataUrlValue)) {
				const datasetPromise = fetch(this.dataUrlValue, {
					headers: {
						Accept: 'application/json',
					},
				})
					.then((response) => {
						if (!response.ok) {
							throw new Error(`Map data request failed with status ${response.status}`);
						}

						return response.json();
					})
					.catch((error) => {
						window.__alumniMapDatasetPromises.delete(this.dataUrlValue);
						throw error;
					});

				window.__alumniMapDatasetPromises.set(this.dataUrlValue, datasetPromise);
			}

			return window.__alumniMapDatasetPromises.get(this.dataUrlValue);
		}

		async loadGoogleMaps() {
			if (window.google?.maps) return window.google.maps;
			if (!this.apiKeyValue) return null;

			if (!window.__alumniGoogleMapsPromise) {
				window.__alumniGoogleMapsPromise = new Promise((resolve, reject) => {
					const callbackName = `__alumniGoogleMapsInit${Date.now()}`;
					const script = document.createElement('script');

					window[callbackName] = () => {
						delete window[callbackName];
						resolve(window.google.maps);
					};

					script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
						this.apiKeyValue,
					)}&callback=${callbackName}`;
					script.async = true;
					script.defer = true;
					script.onerror = () => {
						delete window[callbackName];
						reject(new Error('Failed to load Google Maps API'));
					};

					document.head.append(script);
				});
			}

			return window.__alumniGoogleMapsPromise;
		}

		shouldQueueCoordinateUpdate(markerData) {
			if (this.hasSubmittedCoordinateUpdates) return false;
			if (!markerData?.id || !markerData?.type) return false;
			return true;
		}

		queueCoordinateUpdate(markerData) {
			if (!this.shouldQueueCoordinateUpdate(markerData)) return;

			const latitude = Number(markerData.location?.lat);
			const longitude = Number(markerData.location?.lng);
			if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

			const key = `${markerData.type}:${markerData.id}`;
			this.coordinateUpdateQueue.set(key, {
				id: String(markerData.id),
				type: String(markerData.type),
				lat: latitude,
				lng: longitude,
			});
		}

		async flushCoordinateUpdates() {
			if (this.hasSubmittedCoordinateUpdates) return;
			this.hasSubmittedCoordinateUpdates = true;

			const endpointUrl = this.updateCoordinatesUrlValue;
			if (!endpointUrl || !this.coordinateUpdateQueue.size) return;

			const coordinates = [...this.coordinateUpdateQueue.values()];
			this.coordinateUpdateQueue.clear();

			try {
				const response = await fetch(endpointUrl, {
					method: 'POST',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
						RequestVerificationToken: this.antiforgeryTokenValue,
					},
					body: JSON.stringify(coordinates),
					credentials: 'same-origin',
					keepalive: true,
				});

				if (!response.ok) {
					console.warn('Failed to update map coordinates', response.status);
				}
			} catch (error) {
				console.warn('Failed to update map coordinates', error);
			}
		}

		configureLayer() {
			const markers = this.dataset?.markers || [];
			const availableLayers = [...new Set(markers.map((marker) => marker.type).filter(Boolean))];
			const preferredLayer = this.defaultLayerValue || this.dataset?.settings?.defaultLayer;

			this.activeLayer = preferredLayer || availableLayers[0] || null;
			if (!availableLayers.includes(this.activeLayer)) this.activeLayer = availableLayers[0] || null;
			if (!this.isAuthenticated) this.alumniScope = 'origin';

			this.filterKeys = this.getLayerFilterKeys();
		}

		configureLayerSelect() {
			if (!this.hasLayerSelectTarget) return;

			const markers = this.dataset?.markers || [];
			const availableLayers = new Set(markers.map((marker) => marker.type).filter(Boolean));

			Array.from(this.layerSelectTarget.options).forEach((option) => {
				const hasData = availableLayers.has(option.value);
				option.hidden = !hasData;
				option.disabled = !hasData;
			});

			this.layerSelectTarget.value = this.activeLayer || '';
			this.syncSearchableSelect(this.layerSelectTarget);
		}

		configureAccessNote() {
			if (!this.hasAccessNoteTarget) return;

			this.accessNoteTarget.textContent = this.isAuthenticated
				? this.text(
						'registeredAccessNote',
						'Registered users see more detailed content and can switch Alumni origin or residence.',
				  )
				: this.text(
						'guestAccessNote',
						'Guests see limited content. Alumni are aggregated by country of origin and employer listings have no tooltips.',
				  );
		}

		syncLayerUi() {
			if (this.hasLayerSelectTarget) {
				this.layerSelectTarget.value = this.activeLayer;
				this.syncSearchableSelect(this.layerSelectTarget);
			}

			if (this.hasAuthModeInputTarget) {
				this.authModeInputTargets.forEach((input) => {
					input.checked = input.value === (this.isAuthenticated ? 'registered' : 'guest');
				});
			}

			if (this.hasAlumniScopePanelTarget) {
				const shouldShowScopeSwitch = this.activeLayer === 'alumni' && this.isAuthenticated;
				this.alumniScopePanelTarget.classList.toggle('hidden', !shouldShowScopeSwitch);
			}

			if (this.hasAlumniScopeButtonTarget) {
				this.alumniScopeButtonTargets.forEach((button) => {
					const isActive = button.dataset.mapScope === this.alumniScope;
					const isLight = this.surfaceValue === 'light';
					button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
					button.classList.toggle('bg-blue-light', isActive);
					// Light surface keeps both labels dark; dark surface uses white text on inactive.
					button.classList.toggle('text-blue-dark', isActive || isLight);
					button.classList.toggle('bg-white/10', !isActive && !isLight);
					button.classList.toggle('bg-blue-light/10', !isActive && isLight);
					button.classList.toggle('text-white', !isActive && !isLight);
				});
			}
		}

		getLayerMarkers() {
			if (!this.activeLayer) return [];
			return (this.dataset?.markers || []).filter((marker) => marker.type === this.activeLayer);
		}

		getLayerFilterKeys() {
			const keys = [];

			this.getLayerMarkers().forEach((marker) => {
				Object.keys(marker.filter || {}).forEach((key) => {
					if (!keys.includes(key)) keys.push(key);
				});
			});

			return keys.slice(0, 2);
		}

		populateFilters() {
			this.selectTargets.forEach((select) => {
				const filterKey = select.dataset.mapFilterKey;
				const label = select.dataset.mapFilterLabel || this.toPascalCase(filterKey);
				this.populateSelect(select, filterKey, label);
			});
		}

		populateSelect(select, filterKey, fallbackLabel) {
			if (!select) return;

			const label = this.findLabelElement(select);
			const container = select.closest('[data-map-filter-container]');
			select.innerHTML = '';

			// Kontrola, zda je filterKey dostupný v datech
			const hasFilterKey = filterKey && this.getFilterOptions(filterKey).length > 0;

			if (!hasFilterKey) {
				if (container) container.style.display = 'none';
				select.disabled = true;
				return;
			}

			if (container) container.style.display = 'block';

			if (label) label.textContent = this.getLabel(filterKey, fallbackLabel);
			select.name = filterKey;
			select.append(this.createOption('', this.getValueLabel('all', this.text('allOptions', 'All options'))));

			this.getFilterOptions(filterKey).forEach((option) => {
				select.append(this.createOption(option.value, option.text));
			});

			select.disabled = false;
			this.syncSearchableSelect(select);
		}

		syncSearchableSelect(select) {
			if (!select) return;
			select.dispatchEvent(new Event('change', { bubbles: true }));
		}

		getFilterOptions(filterKey) {
			const uniqueValues = new Map();

			this.getLayerMarkers().forEach((marker) => {
				(marker.filter?.[filterKey] || []).forEach((value) => {
					if (!value || uniqueValues.has(value)) return;
					uniqueValues.set(value, {
						value,
						text: this.getValueLabel(value, value),
					});
				});
			});

			return [...uniqueValues.values()].sort((left, right) =>
				left.text.localeCompare(right.text, this.dataset?.settings?.culture || 'cs-CZ'),
			);
		}

		createOption(value, text) {
			const option = document.createElement('option');
			option.value = value;
			option.textContent = text;
			return option;
		}

		disableSelects() {
			this.selectTargets.forEach((select) => {
				select.disabled = true;
			});
		}

		initializeMap() {
			if (!this.hasCanvasTarget || !this.googleMaps) return;

			this.map = new this.googleMaps.Map(this.canvasTarget, {
				center: this.resolveInitialCenter(),
				zoom: this.zoomValue || 2,
				styles: GRAYSCALE_MAP_STYLES,
				mapTypeControl: false,
				streetViewControl: false,
				fullscreenControl: false,
			});

			this.infoWindow = new this.googleMaps.InfoWindow();
			this.infoWindow.addListener('closeclick', () => this.resetActiveInfoMarker());
			this.placeholderTarget?.classList.add('hidden');
			this.mapWrapperTarget?.classList.add('is-ready');
		}

		hideMobileNavigationControls() {
			const mapContainer = this.canvasTarget?.parentElement;
			if (!mapContainer) return;

			mapContainer.querySelectorAll('[aria-label], [title]').forEach((control) => {
				const label = `${control.getAttribute('aria-label') || ''} ${control.getAttribute('title') || ''}`.toLowerCase();
				if (/(^|\s)(pan|left|right|up|down)(\s|$)/.test(label)) {
					control.style.display = 'none';
				}
			});
		}

		getMarkerIcon(markerCount, active = false) {
			if (markerCount > 1) {
				return {
					anchor: new this.googleMaps.Point(18, 48),
					labelOrigin: new this.googleMaps.Point(18, 18),
					scaledSize: new this.googleMaps.Size(36, 48),
					url: active ? CLUSTER_MARKER_ICON_ACTIVE_URL : CLUSTER_MARKER_ICON_URL,
				};
			}

			return {
				anchor: new this.googleMaps.Point(12, 32),
				labelOrigin: new this.googleMaps.Point(12, 12),
				scaledSize: new this.googleMaps.Size(24, 32),
				url: active ? MARKER_ICON_ACTIVE_URL : MARKER_ICON_URL,
			};
		}

		getMarkerCountLabel(count) {
			return {
				color: '#0C1838',
				fontSize: '12px',
				fontWeight: '700',
				text: String(count),
			};
		}

		getMarkerDisplayCount(markers) {
			return markers.reduce((total, marker) => total + (Number(marker.markerCount) || 1), 0);
		}

		getClusterDisplayCount(markers, fallbackCount) {
			return markers?.reduce((total, marker) => total + (Number(marker.__itemCount) || 1), 0) || fallbackCount;
		}

		setActiveInfoMarker(marker, markerCount) {
			if (this.activeInfoMarker && this.activeInfoMarker !== marker && this.activeInfoMarkerDefaultIcon) {
				this.activeInfoMarker.setIcon(this.activeInfoMarkerDefaultIcon);
			}

			const defaultIcon = this.getMarkerIcon(markerCount, false);
			const activeIcon = this.getMarkerIcon(markerCount, true);

			marker.setIcon(activeIcon);
			this.activeInfoMarker = marker;
			this.activeInfoMarkerDefaultIcon = defaultIcon;
		}

		resetActiveInfoMarker() {
			if (this.activeInfoMarker && this.activeInfoMarkerDefaultIcon) {
				this.activeInfoMarker.setIcon(this.activeInfoMarkerDefaultIcon);
			}

			this.activeInfoMarker = null;
			this.activeInfoMarkerDefaultIcon = null;
		}

		resolveInitialCenter() {
			return {
				lat: this.latValue || DEFAULT_CENTER.lat,
				lng: this.lngValue || DEFAULT_CENTER.lng,
			};
		}

		getFilteredMarkers() {
			const activeFilters = new Map(this.collectFilters().map((filter) => [filter.name, filter.value]));

			return this.getLayerMarkers().filter((marker) => {
				return Array.from(activeFilters.entries()).every(([name, value]) => {
					const values = marker.filter?.[name] || [];
					return !value || values.includes(value);
				});
			});
		}

		getDisplayMarkers() {
			const markers = this.getFilteredMarkers();

			if (this.activeLayer === 'alumni' && !this.isAuthenticated) {
				return this.aggregateAnonymousAlumni(markers);
			}

			return markers.map((marker) => this.normalizeMarker(marker)).filter(Boolean);
		}

		aggregateAnonymousAlumni(markers) {
			const groups = new Map();

			markers.forEach((marker) => {
				const address = this.getAlumniAddress(marker, 'origin');
				const groupKey = address.country || marker.id;

				if (!groups.has(groupKey)) {
					groups.set(groupKey, {
						id: `alumni-group-${groupKey}`,
						isAggregated: true,
						location: {
							lat: null,
							lng: null,
							address,
						},
						markerCount: 0,
						tooltip: {
							title: groupKey,
						},
					});
				}

				groups.get(groupKey).markerCount += 1;
			});

			return [...groups.values()];
		}

		normalizeMarker(marker) {
			if (!marker) return null;

			if (this.activeLayer === 'alumni') {
				const scope = this.isAuthenticated ? this.alumniScope : 'origin';
				const tooltip = this.enrichAlumniTooltip(this.getVisibleTooltip(marker), marker);
				return {
					...marker,
					location: {
						...marker.location,
						address: this.getAlumniAddress(marker, scope),
					},
					tooltip,
				};
			}

			if (this.activeLayer === 'employer' && !this.isAuthenticated) {
				return {
					...marker,
					tooltip: null,
				};
			}

			return {
				...marker,
				tooltip: this.getVisibleTooltip(marker),
			};
		}

		createClusterRenderer() {
			return {
				render: ({ count, position, markers }) => {
					const displayCount = this.getClusterDisplayCount(markers, count);
					return new this.googleMaps.Marker({
						icon: {
							anchor: new this.googleMaps.Point(18, 48),
							labelOrigin: new this.googleMaps.Point(18, 18),
							scaledSize: new this.googleMaps.Size(36, 48),
							url: CLUSTER_MARKER_ICON_URL,
						},
						label: this.getMarkerCountLabel(displayCount),
						position,
						zIndex: 1000 + displayCount,
					});
				},
			};
		}

		buildGeocodeQueries(address) {
			if (!address) return [];

			const street = this.normalizeGeocodeValue(address.street);
			const zip = this.normalizeGeocodeValue(address.zip);
			const city = this.normalizeGeocodeValue(address.city);
			const region = this.normalizeGeocodeValue(address.region);
			const country = this.normalizeGeocodeValue(address.country);

			const fullAddress = [street, [zip, city].filter(Boolean).join(' '), region, country].filter(Boolean).join(', ');
			const cityOnly = city;
			const countryOnly = country;

			return [...new Set([fullAddress, cityOnly, countryOnly].filter(Boolean))];
		}

		normalizeGeocodeValue(value) {
			if (value === null || value === undefined) return '';
			return String(value).trim();
		}

		async geocodeAddress(addressQuery) {
			if (!addressQuery || !this.googleMaps?.Geocoder) return null;

			if (this.geocodeCache.has(addressQuery)) {
				return this.geocodeCache.get(addressQuery);
			}

			if (!this.geocoder) {
				this.geocoder = new this.googleMaps.Geocoder();
			}

			const lookupPromise = new Promise((resolve) => {
				this.geocoder.geocode({ address: addressQuery }, (results, status) => {
					if (status === 'OK' && Array.isArray(results) && results[0]?.geometry?.location) {
						const location = results[0].geometry.location;
						resolve({ lat: location.lat(), lng: location.lng() });
						return;
					}

					resolve(null);
				});
			});

			this.geocodeCache.set(addressQuery, lookupPromise);
			return lookupPromise;
		}

		async resolveMarkerLocation(markerData) {
			if (!markerData) return null;

			if (Number.isFinite(markerData.location?.lat) && Number.isFinite(markerData.location?.lng)) {
				return markerData;
			}

			const geocodeQueries = this.buildGeocodeQueries(markerData.location?.address);
			if (!geocodeQueries.length) return markerData;

			for (const query of geocodeQueries) {
				const resolvedLocation = await this.geocodeAddress(query);
				if (!resolvedLocation) continue;

				this.queueCoordinateUpdate({
					...markerData,
					location: {
						...(markerData.location || {}),
						lat: resolvedLocation.lat,
						lng: resolvedLocation.lng,
					},
				});

				return {
					...markerData,
					location: {
						...(markerData.location || {}),
						lat: resolvedLocation.lat,
						lng: resolvedLocation.lng,
					},
				};
			}

			return markerData;
		}

		async resolveDisplayMarkers(displayMarkers) {
			const resolvedMarkers = [];

			for (const markerData of displayMarkers) {
				resolvedMarkers.push(await this.resolveMarkerLocation(markerData));
			}

			return resolvedMarkers;
		}

		getCoordinateKey(lat, lng) {
			return `${Number(lat).toFixed(6)}|${Number(lng).toFixed(6)}`;
		}

		groupMarkersByCoordinate(markers) {
			const groupedByCoordinates = new Map();

			markers.forEach((markerData) => {
				const lat = markerData.location?.lat;
				const lng = markerData.location?.lng;
				const key = this.getCoordinateKey(lat, lng);

				if (!groupedByCoordinates.has(key)) {
					groupedByCoordinates.set(key, []);
				}

				groupedByCoordinates.get(key).push(markerData);
			});

			return [...groupedByCoordinates.values()].map((group) => ({
				markers: group,
				position: { lat: group[0].location.lat, lng: group[0].location.lng },
			}));
		}

		shouldShowGroupedTooltip(markers) {
			return markers.some((markerData) => this.shouldShowTooltip(markerData));
		}

		buildGroupedInfoWindowContent(markers) {
			const visibleMarkers = markers.filter((markerData) => this.shouldShowTooltip(markerData));
			if (!visibleMarkers.length) return '';

			return visibleMarkers
				.map((markerData, index) => {
					const itemTitle =
					this.composeAlumniTitle(markerData) ||
					markerData.tooltip?.title ||
					markerData.id ||
					`${this.text('item', 'Item')} ${index + 1}`;
					const body = this.buildInfoWindowContent(markerData);
					return `
						<section style="padding:${index ? '.75rem 0 0' : '0'};margin:${index ? '.75rem 0 0' : '0'};border-top:${
						index ? '1px solid #d7dbe3' : '0'
					};">
							<div style="font-size:.85rem;font-weight:700;color:#51627f;margin:0 0 .35rem;">#${index + 1} ${this.escapeHtml(itemTitle)}</div>
							${body}
						</section>
					`;
				})
				.join('');
		}

		enrichAlumniTooltip(tooltip, marker) {
			if (this.activeLayer !== 'alumni') return tooltip;

			const normalizedTooltip = { ...(tooltip || {}) };
			const fieldOfStudy = marker?.filter?.fieldOfStudy;

			if (
				Array.isArray(fieldOfStudy) &&
				fieldOfStudy.length &&
				(normalizedTooltip.fieldOfStudy === null || normalizedTooltip.fieldOfStudy === undefined)
			) {
				normalizedTooltip.fieldOfStudy = fieldOfStudy;
			}

			return normalizedTooltip;
		}

		getAlumniAddress(marker, scope) {
			const tooltip = marker.tooltip || {};

			if (scope === 'residence') {
				return {
					street: null,
					city: tooltip.residenceCity || marker.location?.address?.city || null,
					zip: null,
					region: marker.location?.address?.region || null,
					country: tooltip.residenceCountry || marker.location?.address?.country || null,
				};
			}

			return {
				street: null,
				city: null,
				zip: null,
				region: null,
				country: marker.tooltip?.countryOfOrigin || marker.location?.address?.country || null,
			};
		}

		getVisibleTooltip(marker) {
			const tooltip = marker.tooltip || {};

			if (this.isAuthenticated) return tooltip;
			if (this.activeLayer === 'employer') return null;

			if (this.activeLayer === 'alumni') {
				const visibleKeys = new Set(['title', ...(marker.visibleAnonymous || [])]);
				return Object.fromEntries(Object.entries(tooltip).filter(([key]) => visibleKeys.has(key)));
			}

			return tooltip;
		}

		async renderMarkers() {
			if (!this.map || !this.googleMaps) return;
			const currentRenderRequestId = ++this.renderRequestId;

			const displayMarkers = this.getDisplayMarkers();
			this._lastDisplayCount = displayMarkers.length;
			const resolvedMarkers = await this.resolveDisplayMarkers(displayMarkers);

			if (currentRenderRequestId !== this.renderRequestId || !this.map || !this.googleMaps) return;

			if (this.clusterer) {
				this.clusterer.clearMarkers();
				this.clusterer = null;
			}
			this.resetActiveInfoMarker();
			this.markerInstances.forEach((marker) => marker.setMap(null));
			this.markerInstances = [];

			const mappableMarkers = resolvedMarkers.filter(
				(markerData) => Number.isFinite(markerData.location?.lat) && Number.isFinite(markerData.location?.lng),
			);
			const coordinateGroups = this.groupMarkersByCoordinate(mappableMarkers);

			if (!mappableMarkers.length) {
				this.map.setCenter(this.resolveInitialCenter());
				this.map.setZoom(this.zoomValue || 2);
				this.updateStatus(this.text('noGpsPoints', 'No points with GPS'));
				return;
			}

			const bounds = new this.googleMaps.LatLngBounds();

			coordinateGroups.forEach(({ markers, position }) => {
				const markerData = markers[0];
				const markerCount = markers.length;
				const displayCount = this.getMarkerDisplayCount(markers);
				const markerIcon = this.getMarkerIcon(displayCount);
				const markerLabel =
					displayCount > 1
						? this.getMarkerCountLabel(displayCount)
						: undefined;

				const marker = new this.googleMaps.Marker({
					icon: markerIcon,
					label: markerLabel,
					position,
					title:
						displayCount > 1
							? `${displayCount} ${this.text('items', 'items')}`
							: this.composeAlumniTitle(markerData) || markerData.tooltip?.title || markerData.id,
				});
				marker.__itemCount = displayCount;

				if (markerCount > 1 && this.shouldShowGroupedTooltip(markers)) {
					marker.addListener('click', () => {
						this.setActiveInfoMarker(marker, displayCount);
						this.infoWindow?.setContent(this.buildGroupedInfoWindowContent(markers));
						this.infoWindow?.open({ anchor: marker, map: this.map });
					});
				} else if (this.shouldShowTooltip(markerData)) {
					marker.addListener('click', () => {
						this.setActiveInfoMarker(marker, displayCount);
						this.infoWindow?.setContent(this.buildInfoWindowContent(markerData));
						this.infoWindow?.open({ anchor: marker, map: this.map });
					});
				}

				bounds.extend(position);
				this.markerInstances.push(marker);
			});

			this.clusterer = new MarkerClusterer({
				map: this.map,
				markers: this.markerInstances,
				renderer: this.createClusterRenderer(),
			});

			if (mappableMarkers.length === 1) {
				this.map.setCenter(bounds.getCenter());
				this.map.setZoom(4);
			} else {
				this.map.fitBounds(bounds, 80);
			}

			this.updateStatus(`${mappableMarkers.length} ${this.text('placesOnMap', 'places on map')}`);
		}

		collectFilters() {
			return this.selectTargets
				.map((select) => ({
					name: select.name,
					label: this.findLabel(select),
					value: select.value,
					text: select.options[select.selectedIndex]?.text || '',
				}))
				.filter((filter) => filter.name && filter.value);
		}

		renderSummary(filters = this.collectFilters(), emptyText = this.text('noActiveFilters', 'There are no active filters yet.')) {
			if (!this.hasSummaryTarget) return;

			const layerLabel = this.getValueLabel(this.activeLayer, this.activeLayer || this.getLabel('mapLayer', this.text('mapLayer')));
			const total = this.getLayerMarkers().length;
			const displayed = this.getDisplayMarkers().length;
			const scopeText =
				this.activeLayer === 'alumni'
					? ` ${this.text('mode', 'Mode')}: ${
							this.alumniScope === 'residence'
								? this.getLabel('countryOfResidence', this.text('countryOfResidence'))
								: this.getLabel('countryOfOrigin', this.text('countryOfOrigin'))
					  }.`
					: '';

			if (!filters.length) {
				this.summaryTarget.textContent = `${emptyText} ${this.text(
					'activeLayer',
					'Active layer',
				)}: ${layerLabel}.${scopeText} ${this.text('loadedSourceItems', 'Loaded source items')}: ${total}, ${this.text(
					'displayedItems',
					'displayed',
				)}: ${displayed}.`;
				return;
			}

			this.summaryTarget.textContent = `${layerLabel}.${scopeText} ${filters
				.map((filter) => `${filter.label}: ${filter.text}`)
				.join(' | ')}`;
		}

		findLabel(select) {
			return this.findLabelElement(select)?.textContent?.trim() || select.name;
		}

		findLabelElement(select) {
			const id = select.getAttribute('id');
			return this.filterPanelTarget?.querySelector(`label[for="${id}"]`) || null;
		}

		buildInfoWindowContent(marker) {
			if (marker.isAggregated) {
				const address = this.formatAddress(marker.location?.address);
				const rows = [
					this.buildTooltipInfoRow({
						icon: 'u',
						label: this.escapeHtml(this.getLabel('count', this.text('alumniCount', 'Alumni count'))),
						valueHtml: this.escapeHtml(marker.markerCount),
					}),
					address
						? this.buildTooltipInfoRow({
								icon: 'm',
								label: this.escapeHtml(this.getLabel('location', this.text('venue', 'Venue'))),
								valueHtml: this.escapeHtml(address),
						  })
						: '',
				]
					.filter(Boolean)
					.join('');

				return this.buildTooltipCard({
					eyebrow: this.escapeHtml(this.getValueLabel('alumni', 'Alumni')),
					title: this.escapeHtml(marker.tooltip?.title || 'Alumni'),
					rows,
					linkUrl: marker.tooltip?.url || '',
				});
			}

			if (this.activeLayer === 'event') {
				return this.buildEventInfoWindowContent(marker);
			}

			if (this.activeLayer === 'employer') {
				return this.buildEmployerInfoWindowContent(marker);
			}

			const tooltip = marker.tooltip || {};
			const title = this.composeAlumniTitle(marker) || tooltip.title || marker.id;
			const isNoValue = (value) =>
				value === 'NOVALUE' || (Array.isArray(value) && value.length === 1 && value[0] === 'NOVALUE');
			const isEmptyValue = (value) =>
				value === '' || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && !value.length);
			const rows = Object.entries(tooltip)
				.filter(
					([key, value]) =>
						!['title', 'titlePrefix', 'titleSuffix', 'firstName', 'lastName', 'url', 'detailUrl', 'residenceCity', 'fieldOfStudy'].includes(key) &&
						value !== null &&
						value !== undefined &&
						!isEmptyValue(value) &&
						!isNoValue(value),
				)
				.map(([key, value], index) => this.buildTooltipRow(key, value, index))
				.filter(Boolean)
				.join('');

			return this.buildTooltipCard({
				eyebrow: this.escapeHtml(this.getValueLabel('alumni', 'Alumni')),
				title: this.escapeHtml(title),
				rows,
				linkUrl: tooltip.detailUrl || tooltip.url || '',
			});
		}

		buildEventInfoWindowContent(marker) {
			const tooltip = marker.tooltip || {};
			const title = tooltip.title || marker.id;
			const dateRange = this.formatEventDateRange(tooltip.dateFrom, tooltip.dateTo);
			const contactName = tooltip.contactName || tooltip.contactPerson || '';
			const contactEmail = tooltip.contactEmail || tooltip.email || '';
			const address = marker.location?.address;
			const hasAddressDetails = Boolean(address?.street || address?.city);
			const locationText = hasAddressDetails ? this.formatAddress(address) : '';
			const hasValue = (value) => (Array.isArray(value) ? value.length > 0 : Boolean(value));
			const rows = [
				tooltip.venue ? { type: 'field', key: 'venue', value: tooltip.venue } : null,
				tooltip.university ? { type: 'field', key: 'university', value: tooltip.university } : null,
				tooltip.graduates ? { type: 'field', key: 'graduates', value: tooltip.graduates } : null,
				dateRange
					? {
							type: 'custom',
							icon: 'd',
							label: this.escapeHtml(this.getLabel('eventDateRange', this.text('eventDateRange', 'Event date'))),
							valueHtml: this.escapeHtml(dateRange),
					  }
					: null,
				tooltip.eventType ? { type: 'field', key: 'eventType', value: tooltip.eventType } : null,
				tooltip.targetGroups ? { type: 'field', key: 'targetGroups', value: tooltip.targetGroups } : null,
				locationText ? { type: 'location', value: locationText } : null,
			]
				.filter(Boolean)
				.map((entry, index) => {
					if (entry.type === 'custom') {
						return this.buildTooltipInfoRow({
							icon: entry.icon,
							label: entry.label,
							valueHtml: entry.valueHtml,
							rowIndex: index,
						});
					}

					if (entry.type === 'location') {
						return this.buildTooltipInfoRow({
							icon: 'm',
							label: this.escapeHtml(this.getLabel('location', this.text('location', 'Location'))),
							valueHtml: this.escapeHtml(entry.value),
							rowIndex: index,
						});
					}

					return this.buildTooltipRow(entry.key, entry.value, index);
				})
				.join('');

			return this.buildTooltipCard({
				eyebrow: this.escapeHtml(this.getValueLabel('event', this.text('eventLayer', 'Event'))),
				title: this.escapeHtml(title),
				highlight: this.escapeHtml(dateRange),
				contact: this.escapeHtml(contactName),
				email: this.escapeHtml(contactEmail),
				rows,
				linkUrl: tooltip.url || '',
			});
		}

		buildEmployerInfoWindowContent(marker) {
			const tooltip = marker.tooltip || {};
			const title = tooltip.title || marker.id;
			const listingUrl = this.employerListingUrlValue || '/employer-listing';
			const address = marker.location?.address;
			const hasAddressDetails = Boolean(address?.street || address?.city);
			const locationText = hasAddressDetails ? this.formatAddress(address) : '';
			const hasValue = (value) => (Array.isArray(value) ? value.length > 0 : Boolean(value));
			const rows = [
				tooltip.nace ? { type: 'field', key: 'nace', value: tooltip.nace } : null,
				tooltip.typesOfWork ? { type: 'field', key: 'typesOfWork', value: tooltip.typesOfWork } : null,
				tooltip.workMode ? { type: 'field', key: 'workMode', value: tooltip.workMode } : null,
				locationText ? { type: 'location', value: locationText } : null,
			]
				.filter(Boolean)
				.map((entry, index) => {
					if (entry.type === 'location') {
						return this.buildTooltipInfoRow({
							icon: 'm',
							label: this.escapeHtml(this.getLabel('location', this.text('location', 'Location'))),
							valueHtml: this.escapeHtml(entry.value),
							rowIndex: index,
						});
					}

					return this.buildTooltipRow(entry.key, entry.value, index);
				})
				.join('');

			return this.buildTooltipCard({
				eyebrow: this.escapeHtml(this.getValueLabel('employer', this.getLabel('employer', this.text('employer')))),
				title: this.escapeHtml(title),
				highlight: this.escapeHtml(tooltip.position || ''),
				rows,
				linkUrl: listingUrl,
			});
		}

		formatEventDateRange(dateFrom, dateTo) {
			const from = this.formatEventDate(dateFrom);
			const to = this.formatEventDate(dateTo);

			if (from && to) {
				return from === to ? from : `${from} - ${to}`;
			}

			return from || to || '';
		}

		formatEventDate(value) {
			if (!value) return '';
			const parsed = new Date(value);
			if (Number.isNaN(parsed.getTime())) return String(value);
			return parsed.toLocaleDateString(this.dataset?.settings?.culture || 'cs-CZ');
		}

		shouldShowTooltip(marker) {
			if (marker.isAggregated) return true;
			if (this.activeLayer === 'employer' && !this.isAuthenticated) return false;
			return Boolean(marker.tooltip && Object.keys(marker.tooltip).length);
		}

		composeAlumniTitle(marker) {
			if (this.activeLayer !== 'alumni' || !marker?.tooltip) return marker?.tooltip?.title || null;

			const tooltip = marker.tooltip;
			const firstName = tooltip.firstName || '';
			const lastName = tooltip.lastName || '';
			const fullName = `${firstName} ${lastName}`.trim() || tooltip.title || '';
			const prefix = tooltip.titlePrefix || '';
			const suffix = tooltip.titleSuffix || '';

			return (
				[prefix, fullName, suffix]
					.filter(Boolean)
					.join(' ')
					.trim() || null
			);
		}

		buildTooltipRow(key, value, rowIndex = 0) {
			const label = this.getLabel(key, key);

			if (key === 'email' && typeof value === 'string') {
				return this.buildTooltipInfoRow({
					icon: '@',
					label: this.escapeHtml(label),
					valueHtml: `<a href="mailto:${this.escapeHtml(
						value,
					)}" style="color:#60a5fa;text-decoration:underline;word-break:break-word;">${this.escapeHtml(value)}</a>`,
					rowIndex,
				});
			}

			if (key === 'phone' && typeof value === 'string') {
				const dial = value.replace(/\s+/g, '');
				return this.buildTooltipInfoRow({
					icon: 't',
					label: this.escapeHtml(label),
					valueHtml: `<a href="tel:${this.escapeHtml(
						dial,
					)}" style="color:#60a5fa;text-decoration:underline;word-break:break-word;">${this.escapeHtml(value)}</a>`,
					rowIndex,
				});
			}

			if (['linkedin', 'facebook', 'instagram'].includes(key) && typeof value === 'string') {
				return this.buildTooltipInfoRow({
					icon: '#',
					label: this.escapeHtml(label),
					valueHtml: `<a href="${this.escapeHtml(
						value,
					)}" target="_blank" rel="noopener noreferrer" style="color:#60a5fa;text-decoration:underline;word-break:break-word;">${this.escapeHtml(
						value,
					)}</a>`,
					rowIndex,
				});
			}

			const text = Array.isArray(value) ? value.map((item) => this.getValueLabel(item, item)).join(', ') : String(value);
			return this.buildTooltipInfoRow({
				icon: this.getTooltipIcon(key),
				label: this.escapeHtml(label),
				valueHtml: this.escapeHtml(text),
				rowIndex,
			});
		}

		buildTooltipCard({ title = '', highlight = '', rows = '', contact = '', email = '', linkUrl = '' }) {
			return `
				<div style="background:#ffffff;color:#0C1838;padding:0 12px;max-width:24rem;line-height:1.42;">
					${title ? `<h3 style="font-size:24px;line-height:28px;font-weight:700;color:#0C1838;margin-bottom:30px;">${title}</h3>` : ''}
					${highlight ? `<div style="font-size:34px;line-height:1;font-weight:700;color:#436AB3;margin-bottom:30px;">${highlight}</div>` : ''}
					${contact ? `<div style="font-size:1.06rem;font-weight:800;color:#0C1838;margin:0 0 .2rem;">${contact}</div>` : ''}
					${
						email
							? `<div style="margin:0 0 .85rem;"><a href="mailto:${email}" style="color:#436AB3;text-decoration:underline;font-size:1.1rem;font-weight:700;word-break:break-word;">${email}</a></div>`
							: ''
					}
					${rows ? `<div style="display:grid;gap:.7rem;margin:.4rem 0 0;border-bottom: 1px solid #436AB3;padding-bottom:8px;">${rows}</div>` : ''}
					${
						linkUrl
							? `<div style="margin:.85rem 0 0;"><a href="${this.escapeHtml(
									linkUrl,
								  )}" target="_blank" rel="noopener noreferrer" style="display:flex;align-items:center;justify-content:space-between;gap:12px;color:#000000;font-size:20px;line-height:32px;font-weight:600;text-decoration:none;"><span style="min-width:0;word-break:break-word;">${this.escapeHtml(this.getLabel('url', this.text('url')))}</span><svg width="36" height="17" viewBox="0 0 36 17" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex:0 0 auto;"><path d="M3.07261e-06 8.51157C-6.17881e-05 8.38752 0.0220634 8.26468 0.0651161 8.15006C0.108169 8.03544 0.171306 7.9313 0.250911 7.84358C0.330516 7.75587 0.425029 7.68631 0.529052 7.63887C0.633075 7.59143 0.744567 7.56705 0.857146 7.56712L33.0738 7.56712L27.6798 1.62377C27.5979 1.53665 27.5326 1.43243 27.4877 1.31721C27.4428 1.20198 27.4191 1.07805 27.4181 0.952647C27.4171 0.827245 27.4388 0.702878 27.4819 0.58681C27.525 0.470742 27.5887 0.365292 27.6691 0.276616C27.7496 0.187939 27.8453 0.117812 27.9507 0.0703246C28.056 0.0228374 28.1689 -0.00105789 28.2827 3.11922e-05C28.3965 0.00112028 28.509 0.0271747 28.6135 0.0766722C28.7181 0.12617 28.8127 0.198121 28.8918 0.288325L35.7489 7.84388C35.8285 7.93155 35.8917 8.03565 35.9347 8.15022C35.9778 8.26479 36 8.38759 36 8.5116C36 8.63562 35.9778 8.75842 35.9347 8.87299C35.8917 8.98756 35.8285 9.09165 35.7489 9.17933L28.8918 16.7349C28.7301 16.9069 28.5136 17.0021 28.2889 17C28.0641 16.9978 27.8491 16.8985 27.6902 16.7234C27.5313 16.5483 27.4411 16.3114 27.4392 16.0638C27.4372 15.8161 27.5236 15.5776 27.6798 15.3994L33.0738 9.45601L0.857147 9.45601C0.744567 9.45608 0.633075 9.4317 0.529052 9.38427C0.425029 9.33683 0.330516 9.26727 0.250911 9.17955C0.171306 9.09184 0.108169 8.9877 0.0651162 8.87308C0.0220635 8.75846 -6.17664e-05 8.63561 3.07261e-06 8.51157Z" fill="#E22F27"/></svg></a></div>`
							: ''
					}
				</div>
			`;
		}

		buildTooltipInfoRow({ icon = 'i', label, valueHtml, rowIndex = 0 }) {
			const rowBackground = rowIndex % 2 === 0 ? '#FFFFFF' : '#F7F5EE';
			return `
				<div style="background:${rowBackground};border-radius:10px;overflow:hidden;">
					<div style="display:grid;grid-template-columns:auto 1fr;gap:8px;align-items:start;padding:8px 10px;">
						<span style="display:inline-flex;align-items:center;justify-content:center;width:25px;height:25px;color:#E22F27;line-height:1;flex:0 0 auto;">${this.buildTooltipIcon(
							icon,
						)}</span>
						<div style="min-width:0;">
							<div style="font-weight:600;color:#0C1838;font-size:16px;line-height:24px;">${label}: <span style="font-weight:400;color:#0C1838;font-size:16px;line-height:26px;">${valueHtml}</span></div>
						</div>
					</div>
				</div>
			`;
		}

		buildTooltipIcon(icon) {
			if (icon === 'm') {
				return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 1.25C17.3566 1.25 21.75 5.03039 21.75 10.5C21.75 14.6709 19.8844 17.7333 17.7656 19.7393C16.7091 20.7395 15.5843 21.4832 14.583 21.9795C13.6066 22.4635 12.6707 22.75 12 22.75C11.3293 22.75 10.3934 22.4635 9.41699 21.9795C8.4157 21.4832 7.2909 20.7395 6.23438 19.7393C4.1156 17.7333 2.25 14.6709 2.25 10.5C2.25 5.03039 6.64344 1.25 12 1.25ZM12 2.75C7.35656 2.75 3.75 5.96961 3.75 10.5C3.75 14.1809 5.38446 16.8684 7.26562 18.6494C8.20898 19.5425 9.20939 20.2017 10.083 20.6348C10.9815 21.0801 11.6707 21.25 12 21.25C12.3293 21.25 13.0185 21.0801 13.917 20.6348C14.7906 20.2017 15.791 19.5425 16.7344 18.6494C18.6155 16.8684 20.25 14.1809 20.25 10.5C20.25 5.96961 16.6434 2.75 12 2.75ZM12 7.25C14.0711 7.25 15.75 8.92893 15.75 11C15.75 13.0711 14.0711 14.75 12 14.75C9.92893 14.75 8.25 13.0711 8.25 11C8.25 8.92893 9.92893 7.25 12 7.25ZM12 8.75C10.7574 8.75 9.75 9.75736 9.75 11C9.75 12.2426 10.7574 13.25 12 13.25C13.2426 13.25 14.25 12.2426 14.25 11C14.25 9.75736 13.2426 8.75 12 8.75Z" fill="#E22F27"/></svg>`;
			}

			if (icon === 'i') {
				return `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.1961 11.5697C13.1961 11.1844 12.8838 10.8721 12.4985 10.8721C12.1132 10.8721 11.8008 11.1844 11.8008 11.5697V17.1511C11.8008 17.5364 12.1132 17.8488 12.4985 17.8488C12.8838 17.8488 13.1961 17.5364 13.1961 17.1511V11.5697Z" fill="#E22F27"/><path fill-rule="evenodd" clip-rule="evenodd" d="M12.5 2.5C6.97715 2.5 2.5 6.97715 2.5 12.5C2.5 18.0229 6.97715 22.5 12.5 22.5C18.0229 22.5 22.5 18.0229 22.5 12.5C22.5 6.97715 18.0229 2.5 12.5 2.5ZM3.89535 12.5C3.89535 7.74779 7.74779 3.89535 12.5 3.89535C17.2522 3.89535 21.1047 7.74779 21.1047 12.5C21.1047 17.2522 17.2522 21.1047 12.5 21.1047C7.74779 21.1047 3.89535 17.2522 3.89535 12.5Z" fill="#E22F27"/><path d="M13.4308 8.77887C13.4308 9.29261 13.0143 9.7091 12.5005 9.7091C11.9868 9.7091 11.5703 9.29261 11.5703 8.77887C11.5703 8.26512 11.9868 7.84863 12.5005 7.84863C13.0143 7.84863 13.4308 8.26512 13.4308 8.77887Z" fill="#E22F27"/></svg>`;
			}

			return `<span style="display:inline-flex;align-items:center;justify-content:center;width:25px;height:25px;border:2px solid #E22F27;border-radius:999px;color:#E22F27;font-size:.8rem;font-weight:900;line-height:1;">${this.escapeHtml(
				icon,
			)}</span>`;
		}

		getTooltipIcon(key) {
			if (['university', 'faculty', 'countryOfOrigin', 'residenceCountry', 'location', 'venue'].includes(key)) return 'i';
			if (['graduates', 'fieldOfStudy', 'eventType', 'workMode'].includes(key)) return 'u';
			if (['nace'].includes(key)) return 'n';
			return 'o';
		}

		getLabel(key, fallback) {
			return this.dataset?.settings?.labels?.[key] || this.labelsValue?.[key] || fallback;
		}

		formatAddress(address) {
			if (!address) return '';
			return [address.street, [address.zip, address.city].filter(Boolean).join(' '), address.region, address.country]
				.filter((item) => item && String(item).trim())
				.join(', ');
		}

		getValueLabel(value, fallback) {
			if (!value) return fallback;
			const labelKey = `value${this.toPascalCase(value)}`;
			return this.dataset?.settings?.labels?.[labelKey] || fallback;
		}

		toPascalCase(value) {
			return String(value)
				.split(/[^a-zA-Z0-9]+/)
				.filter(Boolean)
				.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
				.join('');
		}

		escapeHtml(value) {
			return String(value)
				.replaceAll('&', '&amp;')
				.replaceAll('<', '&lt;')
				.replaceAll('>', '&gt;')
				.replaceAll('"', '&quot;')
				.replaceAll("'", '&#39;');
		}

		toggleFullscreen() {
			if (!this.hasMapWrapperTarget) return;

			const fullscreenElement = document.fullscreenElement;

			if (fullscreenElement) {
				// Pokud je něco už v fullscreenu, zavři to a zobraz overlay
				document.exitFullscreen().catch((err) => {
					console.error('Error exiting fullscreen:', err);
				});
				if (this.hasFullscreenOverlayTarget) {
					this.fullscreenOverlayTarget.classList.remove('hidden');
				}
			} else {
				// Spusť fullscreen pro mapWrapper a skryj overlay
				this.mapWrapperTarget.parentElement.requestFullscreen().catch((err) => {
					console.error('Error entering fullscreen:', err);
				});
				if (this.hasFullscreenOverlayTarget) {
					this.fullscreenOverlayTarget.classList.add('hidden');
				}
			}
		}

		toggleFilters() {
			if (!this.hasFilterPanelTarget) return;

			if (window.innerWidth < 768) {
				this.filterPanelTarget.classList.toggle('hidden');
				const isHidden = this.filterPanelTarget.classList.contains('hidden');
				this.updateFilterToggleButtons(isHidden);
				return;
			}

			const isCollapsed = this.filterPanelTarget.dataset.collapsed === 'true';
			this.setDesktopFilterPanelCollapsed(!isCollapsed);
		}

		setDesktopFilterPanelCollapsed(collapsed) {
			const panel = this.filterPanelTarget;
			const layout = panel.parentElement;
			const header = panel.firstElementChild;
			const title = header?.querySelector('h3');
			const desktopToggle = header?.querySelector('[data-map-target="toggleFilterButton"]');
			const panelBody = Array.from(panel.children).slice(1);

			panel.dataset.collapsed = collapsed ? 'true' : 'false';
			panel.style.overflow = collapsed ? 'hidden' : '';
			panel.style.width = collapsed ? '5.75rem' : '';
			panel.style.minWidth = collapsed ? '5.75rem' : '';
			panel.style.flex = collapsed ? '0 0 5.75rem' : '';

			if (layout) {
				layout.style.gridTemplateColumns = collapsed ? '5.75rem minmax(0, 1fr)' : '';
			}

			if (header) {
				header.style.justifyContent = collapsed ? 'center' : '';
				header.style.borderBottomWidth = collapsed ? '0' : '';
				header.style.paddingBottom = collapsed ? '0' : '';
			}

			if (title) {
				title.style.display = collapsed ? 'none' : '';
			}

			if (desktopToggle) {
				desktopToggle.style.display = 'flex';
				desktopToggle.style.width = collapsed ? '100%' : '';
				desktopToggle.style.justifyContent = collapsed ? 'center' : '';
			}

			panelBody.forEach((element) => {
				element.style.display = collapsed ? 'none' : '';
			});

			this.updateFilterToggleButtons(collapsed);
		}

		updateFilterToggleButtons(showFilters) {
			this.toggleFilterButtonTargets.forEach((button) => {
				const showText = button.dataset.mapShowText;
				const hideText = button.dataset.mapHideText;
				const textSpan = button.querySelector('[data-text-content]');
				const arrow = button.querySelector('.icon-arrow-right, .icon-arrow-left');
				const chevron = button.querySelector('.icon-chevron-down');
				if (textSpan) {
					textSpan.textContent = showFilters ? showText : hideText;
				}
				button.setAttribute('aria-expanded', showFilters ? 'false' : 'true');
				if (arrow) {
					arrow.style.display = showFilters ? 'none' : '';
				}
				if (chevron) {
					chevron.classList.toggle('rotate-180', !showFilters);
				}
			});
		}

		text(key, fallback = '') {
			return this.labelsValue?.[key] || fallback;
		}

		updateStatus(text) {
			if (this.hasStatusTarget) this.statusTarget.textContent = text;
			if (this.hasLiveStatusTarget) this.liveStatusTarget.textContent = text;
		}
	};
};
